import React,{useState,useEffect,useContext,useCallback,useMemo,useRef} from 'react'
import {Link,useNavigate,useParams} from 'react-router-dom'
import axios from 'axios'
import moment from 'moment'
import {UserContext} from '../context/UserContext'
import Btn from '../ui/Btn'
import UiAvatar from '../ui/Avatar'
import ChatThread from './ChatThread'
import {isTrial} from '../access'
import './inbox.css'
import {toPlain} from './chatText'

/*
 * Inbox — every conversation the scout has with players, in one place.
 *
 * Left: the conversations, newest activity first, with the last thing said
 * and how many of the player's messages are unread. Right: the conversation
 * itself (ChatThread). On a phone only one of the two shows at a time.
 *
 * The list follows new messages as they arrive: the scout's socket joins
 * every one of its rooms, so a message in any of them refreshes the list.
 * A slow poll covers the times the socket is down.
 */

const api=(path)=>process.env.REACT_APP_API_SERVER+path
const auth=()=>({headers:{'Authorization':'Bearer '+sessionStorage.getItem('mngapiToken')}})
const POLL_MS=30000

/* Tells the sidebar badge the new total without it asking again. */
export function announceUnread(n){
  try { window.dispatchEvent(new CustomEvent('inbox-unread',{detail:Number(n)||0})) } catch (e) {}
}

function when(d){
  const m=moment(d)
  if (!m.isValid()) return ''
  if (m.isSame(moment(),'day')) return m.format('HH:mm')
  if (m.isAfter(moment().subtract(6,'days').startOf('day'))) return m.format('ddd')
  if (m.isSame(moment(),'year')) return m.format('D MMM')
  return m.format('DD/MM/YY')
}

const IconSearch=(
  <svg className="ib-search-ico" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>
  </svg>
)

export default function Inbox() {
  const {socket}=useContext(UserContext)
  const {roomUUID}=useParams()
  const navigate=useNavigate()
  const [rooms,setRooms]=useState([])
  const [state,setState]=useState('loading') // loading | ready | error | trial
  const [query,setQuery]=useState('')
  const joined=useRef(new Set())
  const timer=useRef(null)

  const load=useCallback((quiet)=>{
    if (!quiet) setState(s=>s==='ready'?s:'loading')
    axios.get(api('manager/rooms/inbox'),auth())
      .then(res=>{
        const list=res.data.rooms||[]
        setRooms(list)
        setState('ready')
        announceUnread(res.data.unread)
        if (socket){
          list.forEach(r=>{
            if (!joined.current.has(r.RoomUUID)){
              joined.current.add(r.RoomUUID)
              socket.emit('join-room',r.RoomUUID)
            }
          })
        }
      })
      .catch(err=>{
        const r=err&&err.response
        if (r&&r.status===403&&r.data&&r.data.code==='TRIAL_LIMIT') setState('trial')
        else if (!quiet) setState('error')
      })
  },[socket])

  /* Many events can land together (a message, its read receipt, the poll);
     one reload a moment later covers them all. */
  const refresh=useCallback(()=>{
    clearTimeout(timer.current)
    timer.current=setTimeout(()=>load(true),400)
  },[load])

  useEffect(()=>{
    if (isTrial()){ setState('trial'); return undefined }
    load(false)
    const poll=setInterval(()=>load(true),POLL_MS)
    return ()=>{ clearInterval(poll); clearTimeout(timer.current) }
  },[load])

  useEffect(()=>{
    if (!socket) return undefined
    const onAny=()=>refresh()
    socket.on('receive-message',onAny)
    socket.on('chat-notification',onAny)
    /* After a reconnect the server has forgotten which rooms we were in. */
    const onConnect=()=>{
      joined.current=new Set()
      refresh()
    }
    socket.on('connect',onConnect)
    return ()=>{
      socket.off('receive-message',onAny)
      socket.off('chat-notification',onAny)
      socket.off('connect',onConnect)
    }
  },[socket,refresh])

  const shown=useMemo(()=>{
    const q=query.trim().toLowerCase()
    if (!q) return rooms
    return rooms.filter(r=>
      (r.ChatWithName||'').toLowerCase().includes(q)||
      toPlain(r.LastMessage).toLowerCase().includes(q)||
      (r.Country||'').toLowerCase().includes(q))
  },[rooms,query])

  const unreadTotal=rooms.reduce((s,r)=>s+r.Unread,0)
  const open=roomUUID||''

  if (state==='trial'){
    return (
      <div className="ib-page">
        <section className="panel ib-locked">
          <div className="ib-locked-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
          </div>
          <h2 className="ib-locked-title">Messaging is part of a plan</h2>
          <p className="ib-locked-text">During the free trial you can browse players and watch their videos. Choose a plan to message players and keep every conversation here.</p>
          <Link className="btn-ui btn-accent" to="/plans">See the plans</Link>
        </section>
      </div>
    )
  }

  return (
    <div className="ib-page">
      <section className={'panel ib'+(open?' ib-has-open':'')}>

        <aside className="ib-list" aria-label="Conversations">
          <div className="ib-list-head">
            <div className="ib-list-title">
              <h2 className="p-title">Conversations</h2>
              <span className="mono ib-count">
                {state==='ready'
                  ?(unreadTotal>0?unreadTotal+' unread':rooms.length+(rooms.length===1?' chat':' chats'))
                  :''}
              </span>
            </div>
            <div className="ib-search">
              {IconSearch}
              <input
                className="ib-search-input"
                type="search"
                placeholder="Search players or messages"
                value={query}
                onChange={e=>setQuery(e.target.value)}
                aria-label="Search conversations"
              />
            </div>
          </div>

          <div className="ib-items">
            {state==='loading'
              ?[0,1,2,3].map(i=>(
                  <div className="ib-item ib-item-sk" key={i} aria-hidden="true">
                    <span className="skeleton ib-sk-av"></span>
                    <span className="ib-sk-lines"><span className="skeleton"></span><span className="skeleton"></span></span>
                  </div>
                ))
              :null}

            {state==='error'
              ?<div className="ib-empty">
                  <p>The conversations could not be loaded.</p>
                  <Btn onClick={()=>load(false)}>Try again</Btn>
                </div>
              :null}

            {state==='ready'&&rooms.length===0
              ?<div className="ib-empty">
                  <p><b>No conversations yet.</b></p>
                  <p>Open a player's profile and press <b>Message</b> to start one. Every conversation will be kept here.</p>
                  <Link className="btn-ui" to="/playerslist">Browse players</Link>
                </div>
              :null}

            {state==='ready'&&rooms.length>0&&shown.length===0
              ?<div className="ib-empty"><p>Nothing matches “{query}”.</p></div>
              :null}

            {state==='ready'
              ?shown.map(r=>(
                  <button
                    type="button"
                    key={r.RoomUUID}
                    className={'ib-item'+(r.RoomUUID===open?' is-open':'')+(r.Unread>0?' is-unread':'')}
                    aria-current={r.RoomUUID===open?'true':undefined}
                    onClick={()=>navigate('/inbox/'+r.RoomUUID)}
                  >
                    <UiAvatar uuid={r.ChatWithUUID} name={r.ChatWithName} size={40}/>
                    <span className="ib-item-main">
                      <span className="ib-item-top">
                        <span className="ib-item-name">{r.ChatWithName}</span>
                        <span className="mono ib-item-when">{when(r.LastAt)}</span>
                      </span>
                      <span className="ib-item-bottom">
                        <span className="ib-item-last">
                          {r.LastFromMe?<span className="ib-you">You: </span>:null}
                          {toPlain(r.LastMessage)}
                        </span>
                        {r.Unread>0
                          ?<span className="ib-badge" aria-label={r.Unread+' unread'}>{r.Unread>99?'99+':r.Unread}</span>
                          :null}
                      </span>
                      {r.Country?<span className="mono ib-item-meta">{r.Country}</span>:null}
                    </span>
                  </button>
                ))
              :null}
          </div>
        </aside>

        <div className="ib-main">
          {open
            ?<ChatThread
                key={open}
                roomUUID={open}
                serverTotal={(rooms.find(r=>r.RoomUUID===open)||{}).Total||0}
                onActivity={refresh}
                onBack={()=>navigate('/inbox')}
              />
            :<div className="ib-none">
                <div className="ib-none-ico" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M4 5h16v11H8l-4 4z"/></svg>
                </div>
                <p className="ib-none-title">Select a conversation</p>
                <p className="ib-none-text">
                  {state==='ready'&&rooms.length>0
                    ?(unreadTotal>0
                        ?'You have '+unreadTotal+' unread '+(unreadTotal===1?'message':'messages')+'.'
                        :'You are all caught up.')
                    :'Messages you exchange with players appear here.'}
                </p>
              </div>}
        </div>

      </section>
    </div>
  )
}
